/*
 * SVG of a U-100 insulin syringe with a live fill level and a "draw to here"
 * marker. `units` = how much to draw, `capacity` = barrel max (30/50/100).
 *
 * Orientation matches a real syringe: NEEDLE on the left, "0" at the needle end,
 * liquid fills from the needle outward as the plunger (right) pulls away. Major
 * marks are numbered so the reader can locate the fill line on a real barrel, and
 * an over-capacity draw turns the marker red.
 */
export default function SyringeVisual({ units = 0, capacity = 100 }) {
  const over = units > capacity
  const clamped = Math.max(0, Math.min(units, capacity))
  const frac = capacity > 0 ? clamped / capacity : 0

  // barrel geometry
  const bx = 54
  const bw = 250
  const by = 44
  const bh = 30
  const cy = by + bh / 2
  const fillW = bw * frac
  const fillX = bx + fillW

  // tick spacing mirrors a real barrel of this size:
  // 0.3/0.5 mL are marked every unit; 1 mL every 2 units. Majors get a number.
  const minorStep = capacity <= 50 ? 1 : 2
  const majorStep = capacity <= 30 ? 5 : 10
  const ticks = []
  const numbers = []
  for (let u = 0; u <= capacity; u += minorStep) {
    const x = bx + (bw * u) / capacity
    const major = u % majorStep === 0
    ticks.push(
      <line key={`t${u}`} x1={x} y1={by} x2={x} y2={by + (major ? 10 : 6)} stroke="var(--border-strong)" strokeWidth={major ? 1.1 : 0.7} />
    )
    if (major) {
      numbers.push(
        <text key={`n${u}`} x={x} y={by + bh + 13} textAnchor="middle" fontSize="8.5" fill="var(--text-mute)">{u}</text>
      )
    }
  }

  // "draw to here" marker — a value pill above the fill line, clamped so it never
  // spills past the barrel ends. Red when the draw overflows the barrel.
  const accent = over ? 'var(--red)' : 'var(--blue-500)'
  const shown = over ? units : clamped
  const shownText = `${+shown.toFixed(Number.isInteger(shown) ? 0 : 1)} u`
  const pillW = 44
  const pillH = 17
  const pillCx = Math.max(bx + pillW / 2, Math.min(fillX, bx + bw - pillW / 2))
  const pillY = 8

  return (
    <svg viewBox="0 0 360 118" width="100%" role="img" aria-label={`Insulin syringe: draw ${shownText} of ${capacity} units`}>
      <defs>
        <linearGradient id="peptGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="var(--blue-400)" />
          <stop offset="1" stopColor="var(--navy-700)" />
        </linearGradient>
      </defs>

      {/* needle + cone (left) */}
      <line x1="6" y1={cy} x2="40" y2={cy} stroke="var(--text-mute)" strokeWidth="2" />
      <polygon points={`${bx},${by + 4} 40,${cy} ${bx},${by + bh - 4}`} fill="var(--border-strong)" />

      {/* barrel */}
      <rect x={bx} y={by} width={bw} height={bh} rx="6" fill="var(--surface)" stroke="var(--border-strong)" strokeWidth="1.5" />
      {/* fill — grows from the needle (left) end */}
      <rect x={bx} y={by} width={fillW} height={bh} rx="6" fill="url(#peptGrad)" opacity="0.92" />

      {/* plunger seal at the fill boundary */}
      {fillW > 0 && (
        <rect x={fillX - 3} y={by - 1} width="5" height={bh + 2} rx="1.5" fill="var(--navy-700)" />
      )}

      {/* ticks + numbers */}
      {ticks}
      {numbers}

      {/* plunger rod + thumb rest (right) */}
      <rect x={bx + bw} y={cy - 3} width="38" height="6" rx="3" fill="var(--border-strong)" />
      <rect x={bx + bw + 36} y={by + 4} width="14" height={bh - 8} rx="3" fill="var(--surface-3)" stroke="var(--border-strong)" />

      {/* "draw to here" marker: guide line down to the fill boundary + value pill */}
      <line x1={fillX} y1={pillY + pillH} x2={fillX} y2={by} stroke={accent} strokeWidth="1.2" strokeDasharray="3 2" />
      <polygon points={`${fillX - 4},${by - 5} ${fillX + 4},${by - 5} ${fillX},${by}`} fill={accent} />
      <rect x={pillCx - pillW / 2} y={pillY} width={pillW} height={pillH} rx={pillH / 2} fill={accent} />
      <text x={pillCx} y={pillY + pillH / 2 + 3.5} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#fff">{shownText}</text>

      {/* caption */}
      {over ? (
        <text x={bx + bw / 2} y={by + bh + 30} textAnchor="middle" fontSize="9.5" fill="var(--red)">
          exceeds this {capacity}-unit syringe
        </text>
      ) : (
        <text x={bx + bw / 2} y={by + bh + 30} textAnchor="middle" fontSize="9.5" fill="var(--text-mute)">
          fill to the {shownText} line on a {capacity}-unit barrel
        </text>
      )}
    </svg>
  )
}
